import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { getGetOrganizationByOrganizationIdQueryKey } from '@unity/commerce-bridge';
import { getUnityGetUserContextQueryKey } from '@unity/identity-v3';

/**
 * commerce-core's OrganizationProvider reads the org (`users/org/{org}`) and
 * the user context (`me/context`) in every CartManagementProvider: the header
 * mini-cart's and the PDP's (CPS-7696). Neither changes while a page is open,
 * so they are served from cache, focus included, and each provider shares
 * what it read with the other.
 */
export const COMMERCE_REFERENCE_STALE_TIME_MS = 5 * 60_000;

const REFERENCE_QUERY_KEYS: QueryKey[] = [
  getGetOrganizationByOrganizationIdQueryKey('').slice(0, -1),
  getUnityGetUserContextQueryKey(),
];

const isReferenceQuery = (queryKey: QueryKey) =>
  REFERENCE_QUERY_KEYS.some((prefix) =>
    prefix.every((part, index) => queryKey[index] === part)
  );

const sharingClients = new Set<QueryClient>();

/** Writes `data` unless the client already holds a newer read. */
const offer = (
  client: QueryClient,
  queryKey: QueryKey,
  data: unknown,
  updatedAt: number
) => {
  if ((client.getQueryState(queryKey)?.dataUpdatedAt ?? 0) >= updatedAt) {
    return;
  }
  client.setQueryData(queryKey, data, { updatedAt });
};

/**
 * Call on a new client, before its provider mounts: sets the cache policy
 * and seeds what the page's other commerce providers already read.
 */
export const configureCommerceReferenceQueries = (client: QueryClient) => {
  for (const queryKey of REFERENCE_QUERY_KEYS) {
    client.setQueryDefaults(queryKey, {
      staleTime: COMMERCE_REFERENCE_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    });
  }
  for (const other of sharingClients) {
    for (const { queryKey, state } of other.getQueryCache().getAll()) {
      if (isReferenceQuery(queryKey) && state.status === 'success') {
        offer(client, queryKey, state.data, state.dataUpdatedAt);
      }
    }
  }
};

/**
 * Hands each org or user-context read this client makes to the page's other
 * commerce providers; returns the cleanup for an effect. Writes made
 * straight into the cache (commerce-core's own, or one shared here) are not
 * passed on.
 */
export const shareCommerceReferenceQueries = (
  client: QueryClient
): (() => void) => {
  sharingClients.add(client);
  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (
      event.type !== 'updated' ||
      event.action.type !== 'success' ||
      event.action.manual ||
      !isReferenceQuery(event.query.queryKey)
    ) {
      return;
    }
    const { queryKey, state } = event.query;
    for (const other of sharingClients) {
      if (other !== client) {
        offer(other, queryKey, state.data, state.dataUpdatedAt);
      }
    }
  });
  return () => {
    sharingClients.delete(client);
    unsubscribe();
  };
};
